import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { Dispatch, SetStateAction } from 'react';
import {
  ClientContactField,
  ICandidate,
  ICandidateFilter,
  ICandidateSubmissionSearchFilterVM,
  IClientDTO,
  IJobSuggestions,
  IStageStatusPair,
  ISubmissionsAPIResponse,
  ITeam,
} from 'wisestep-api';
import { formateDateInDateMonthYear, ILocation, WsSelectObj } from 'wisestep-base';

import { IBreadCrumbsList } from '../../../components/WsBreadCrumbs';

export const generateCandidateViewUrl = (candidate: ICandidate, array: string[]) =>
  `/candidate-layout/submissions?id=${candidate.candidateId}&interaction=${candidate.id}&array=${array}`;

export interface ISubmissionsChildProps {
  searchQuery: ICandidateSubmissionSearchFilterVM;
  setSearchQuery?: Dispatch<SetStateAction<ICandidateSubmissionSearchFilterVM>>;
  selectedCandidate?: (candidate: ICandidate[]) => void;
  unSelect?: boolean;
  selectRows?: boolean;
  updateRow?: ICandidate[];
  totalElements?: number;
  setTotalNumber?: (number) => void;
}

export const defaultSubmissionsData: ISubmissionsAPIResponse = {
  content: [],
  empty: true,
  first: true,
  last: true,
  number: 0,
  numberOfElements: 0,
  size: 0,
  totalElements: 0,
  totalPages: 0,
};

export const defaultSubmissionsListingSearchQuery: ICandidateSubmissionSearchFilterVM = {
  workspaceId: '',
  query: '',
  page: 0,
  size: 15,
  filters: [],
  sort: [],
  jobs: [],
};

export const extractBusinessUnitID = (input: string, name: boolean) => {
  const parts = input?.split('&');
  return name ? parts[1]?.trim() ?? '' : parts[0]?.trim() ?? '';
};

export interface ISalaryRange {
  currency: string;
  unitTime: string;
  max: number;
  min: number;
}

export interface ISubmissionAdvanceFilters {
  CLIENT_NAMES: IClientDTO[];
  CLIENT_RECRUITERS: ClientContactField[];
  JOB_TITLE: string[];
  JOB_STATUS: string;
  salaryRange: ISalaryRange;
  JOB_LOCATIONS: ILocation[];
  CANDIDATE_LOCATIONS: ILocation[];
  JOB_TYPE: string[];
  SUBMISSION_X_HOURS_JOB_POSTING: { min: number | null; max: number | null };
  SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB: string[];
  SUBMITTED_TEAM_ASSIGNED_JOB: ITeam[];
  SUBMITTED_TO_JOB_POSTED_TEAM_MEMBER: string[];
  SUBMITTED_TO_JOB_POSTED_TEAM: ITeam[];
  BUSINESS_UNIT: string;
  TAGS: string[];
  RELOCATION_PREFERENCE: string;
  STAGE_STATUS_BETWEEN: string[];
  STAGE_STATUS: IStageStatusPair[];
  stageDateFromDate: string;
  stageDateToDate: string;
  SubmittedOrUpdatedbetween: string;
  SubmittedOrUpdatedFromDate: string;
  SubmittedOrUpdatedToDate: string;
  SUBMITTED_OR_UPDATED_BETWEEN: string[];
  JOB_ID: IJobSuggestions[];
}

export const defaultAdvancedFilterValues: ISubmissionAdvanceFilters = {
  CLIENT_NAMES: [],
  CLIENT_RECRUITERS: [],
  JOB_TITLE: [],
  JOB_STATUS: '',
  salaryRange: {
    currency: '',
    max: 0,
    min: 0,
    unitTime: '',
  },
  JOB_LOCATIONS: [],
  CANDIDATE_LOCATIONS: [],
  JOB_TYPE: [],
  SUBMISSION_X_HOURS_JOB_POSTING: { min: null, max: null },
  SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB: [],
  SUBMITTED_TEAM_ASSIGNED_JOB: [],
  SUBMITTED_TO_JOB_POSTED_TEAM_MEMBER: [],
  SUBMITTED_TO_JOB_POSTED_TEAM: [],
  BUSINESS_UNIT: '',
  TAGS: [],
  RELOCATION_PREFERENCE: '',
  STAGE_STATUS_BETWEEN: [],
  STAGE_STATUS: [],
  stageDateFromDate: '',
  stageDateToDate: '',
  SubmittedOrUpdatedbetween: '',
  SubmittedOrUpdatedFromDate: '',
  SubmittedOrUpdatedToDate: '',
  SUBMITTED_OR_UPDATED_BETWEEN: [],
  JOB_ID: [],
};

export const getDefaultFilterValues = (
  searchQuery: ICandidateSubmissionSearchFilterVM
): ISubmissionAdvanceFilters => {
  const filters = searchQuery.filters;
  if (filters.length < 1) return {} as ISubmissionAdvanceFilters;

  filters.forEach((filter) => {
    switch (filter.filterName) {
      case 'JOB_TITLE':
        defaultAdvancedFilterValues.JOB_TITLE = (filter.values as string[]) ?? [];
        break;
      case 'JOB_STATUS':
        defaultAdvancedFilterValues.JOB_STATUS = filter.values[0] as string;
        break;
      case 'SALARY':
        defaultAdvancedFilterValues.salaryRange = {
          currency: filter?.fields?.salaryCurrency,
          min: filter?.fields?.minSalary,
          max: filter?.fields?.maxSalary,
          unitTime: filter?.fields?.salaryTimeSpan,
        };
        break;
      case 'JOB_LOCATIONS':
        defaultAdvancedFilterValues.JOB_LOCATIONS = (filter.fields as ILocation[]) ?? [];
        break;
      case 'CANDIDATE_LOCATIONS':
        defaultAdvancedFilterValues.CANDIDATE_LOCATIONS = (filter.fields as ILocation[]) ?? [];
        break;
      case 'JOB_TYPE':
        defaultAdvancedFilterValues.JOB_TYPE = filter.values as string[];
        break;
      case 'SUBMISSION_X_HOURS_JOB_POSTING':
        defaultAdvancedFilterValues.SUBMISSION_X_HOURS_JOB_POSTING = {
          min: filter?.fields?.from,
          max: filter?.fields?.to,
        };
        break;
      case 'SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB':
        defaultAdvancedFilterValues.SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB = filter.values as string[];
        break;
      case 'SUBMITTED_TEAM_ASSIGNED_JOB':
        defaultAdvancedFilterValues.SUBMITTED_TEAM_ASSIGNED_JOB =
          filter.values as unknown as ITeam[];
        break;
      case 'SUBMITTED_TO_JOB_POSTED_TEAM_MEMBER':
        defaultAdvancedFilterValues.SUBMITTED_TO_JOB_POSTED_TEAM_MEMBER = filter.values as string[];
        break;
      case 'SUBMITTED_TO_JOB_POSTED_TEAM':
        defaultAdvancedFilterValues.SUBMITTED_TO_JOB_POSTED_TEAM =
          filter.values as unknown as ITeam[];
        break;
      case 'BUSINESS_UNIT':
        defaultAdvancedFilterValues.BUSINESS_UNIT = filter.values[0] as string;
        break;
      case 'TAGS':
        defaultAdvancedFilterValues.TAGS = filter.values as string[];
        break;
      case 'RELOCATION_PREFERENCE':
        defaultAdvancedFilterValues.RELOCATION_PREFERENCE = filter.values[0] as string;
        break;
      case 'STAGE_STATUS_BETWEEN':
        defaultAdvancedFilterValues.stageDateFromDate = filter.values[0] as string;
        defaultAdvancedFilterValues.stageDateToDate = filter.values[1] as string;
        break;
      case 'STAGE_STATUS':
        defaultAdvancedFilterValues.STAGE_STATUS = filter.fields as IStageStatusPair[];
        break;
      case 'SUBMITTED_OR_UPDATED_BETWEEN':
        defaultAdvancedFilterValues.SubmittedOrUpdatedbetween = filters.values[0] as string;
        defaultAdvancedFilterValues.SubmittedOrUpdatedFromDate = filter.values[1] as string;
        defaultAdvancedFilterValues.SubmittedOrUpdatedToDate = filter.values[2] as string;

        break;
    }
  });
  defaultAdvancedFilterValues.JOB_ID = searchQuery.jobs ?? [];

  return defaultAdvancedFilterValues as ISubmissionAdvanceFilters;
};

export const processUserInputsToFilters = (data) => {
  const filters: ICandidateFilter[] = [];
  if (data.JOB_ID?.length > 0) {
    const jobIds = {
      filterName: 'JOB_ID',
      values: data.JOB_ID?.map((job) => job.id),
      fields: {},
    };
    filters.push(jobIds);
  }
  if (
    data.SubmittedOrUpdatedbetween ||
    data.SubmittedOrUpdatedFromDate ||
    data.SubmittedOrUpdatedToDate
  ) {
    const updatedFromDate = data.SubmittedOrUpdatedFromDate
      ? dayjs(data.SubmittedOrUpdatedFromDate).startOf('day').toISOString()
      : null;
    const updatedToDate = data.SubmittedOrUpdatedToDate
      ? dayjs(data.SubmittedOrUpdatedToDate).endOf('day').toISOString()
      : new Date().toISOString();

    const SubmittedOrUpdatedbetweenFilter = {
      filterName: 'SUBMITTED_OR_UPDATED_BETWEEN',
      values: [
        data.SubmittedOrUpdatedbetween ?? 'CREATED_OR_UPDATED_BETWEEN',
        updatedFromDate ?? data.SubmittedOrUpdatedFromDate,
        updatedToDate ?? data.SubmittedOrUpdatedToDate,
      ],
      fields: {},
    };
    filters.push(SubmittedOrUpdatedbetweenFilter);
  }
  if (data.CLIENT_NAMES?.length > 0) {
    const clientFilter = {
      filterName: 'CLIENT_NAMES',
      values: data.CLIENT_NAMES?.map((client) => client.name),
      fields: {},
    };
    filters.push(clientFilter);
  }
  if (data.CLIENT_RECRUITERS?.length > 0) {
    const clientRecruiterFilter = {
      filterName: 'CLIENT_RECRUITERS',
      values: data.CLIENT_RECRUITERS?.map((cr) => cr.emailAddress),
      fields: {},
    };
    filters.push(clientRecruiterFilter);
  }
  if (data.JOB_TITLE?.length > 0) {
    const jobTitleFilter = {
      filterName: 'JOB_TITLE',
      values: data.JOB_TITLE,
      fields: {},
    };
    filters.push(jobTitleFilter);
  }
  if (data.JOB_STATUS?.length > 0 && data.jobStatus != 'All') {
    const jobStatusFilter = {
      filterName: 'JOB_STATUS',
      values: [data.JOB_STATUS != 'Opened'],
      fields: {},
    };
    filters.push(jobStatusFilter);
  }
  if (data.salaryRange?.currency) {
    const salary = data.salaryRange;
    const salaryFilter = {
      filterName: 'SALARY',
      values: data.jobTitle,
      fields: {
        salaryCurrency: salary.currency,
        salaryTimeSpan: salary.unitTime,
        minSalary: salary.min,
        maxSalary: salary.max,
      },
    };
    filters.push(salaryFilter);
  }
  if (data.JOB_LOCATIONS?.length > 0) {
    const jobLocationsFilter = {
      filterName: 'JOB_LOCATIONS',
      values: [],
      locations: data.JOB_LOCATIONS,
    };
    filters.push(jobLocationsFilter);
  }
  if (data.JOB_TYPE?.length > 0) {
    const jobTypeFIlter = {
      filterName: 'JOB_TYPE',
      values: data.JOB_TYPE,
      fields: {},
    };
    filters.push(jobTypeFIlter);
  }
  if (data.CANDIDATE_LOCATIONS?.length > 0) {
    const candidateLocationsFilter = {
      filterName: 'CANDIDATE_LOCATIONS',
      values: [],
      locations: data.CANDIDATE_LOCATIONS,
    };
    filters.push(candidateLocationsFilter);
  }
  if (
    !isEmpty(data.SUBMISSION_X_HOURS_JOB_POSTING.min) &&
    !isEmpty(data.SUBMISSION_X_HOURS_JOB_POSTING.max)
  ) {
    const tatFilter = {
      filterName: 'SUBMISSION_X_HOURS_JOB_POSTING',
      values: [],
      fields: {
        from: data.SUBMISSION_X_HOURS_JOB_POSTING.min,
        to: data.SUBMISSION_X_HOURS_JOB_POSTING.max,
      },
    };
    filters.push(tatFilter);
  }
  if (data.SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB?.length > 0) {
    const teamMemberFilter = {
      filterName: 'SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB',
      values: data.SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB,
      fields: {},
    };
    filters.push(teamMemberFilter);
  }
  if (data.SUBMITTED_TEAM_ASSIGNED_JOB?.length > 0) {
    const teamFilter = {
      filterName: 'SUBMITTED_TEAM_ASSIGNED_JOB',
      values: data.SUBMITTED_TEAM_ASSIGNED_JOB?.map((team: ITeam) => team.id),
      fields: data.SUBMITTED_TEAM_ASSIGNED_JOB?.map((team: ITeam) => team.name),
    };
    filters.push(teamFilter);
  }
  if (data.SUBMITTED_TO_JOB_POSTED_TEAM_MEMBER?.length > 0) {
    const teamFilter = {
      filterName: 'SUBMITTED_TO_JOB_POSTED_TEAM_MEMBER',
      values: data.SUBMITTED_TO_JOB_POSTED_TEAM_MEMBER,
      fields: {},
    };
    filters.push(teamFilter);
  }
  if (data.SUBMITTED_TO_JOB_POSTED_TEAM?.length > 0) {
    const teamFilter = {
      filterName: 'SUBMITTED_TO_JOB_POSTED_TEAM',
      values: data.SUBMITTED_TO_JOB_POSTED_TEAM?.map((team: ITeam) => team.id),
      fields: data.SUBMITTED_TO_JOB_POSTED_TEAM?.map((team: ITeam) => team.name),
    };
    filters.push(teamFilter);
  }
  if (data.BUSINESS_UNIT?.length > 0 && data.BUSINESS_UNIT != 'all') {
    const businessUnitFilter = {
      filterName: 'BUSINESS_UNIT',
      values: [data.BUSINESS_UNIT],
      fields: {},
    };
    filters.push(businessUnitFilter);
  }
  if (data.TAGS?.length > 0) {
    const tagsFilter = {
      filterName: 'TAGS',
      values: data.TAGS,
      fields: {},
    };
    filters.push(tagsFilter);
  }
  if (data.RELOCATION_PREFERENCE?.length > 0) {
    const relocationPreferenceFilter = {
      filterName: 'RELOCATION_PREFERENCE',
      values: data.RELOCATION_PREFERENCE,
      fields: {},
    };
    filters.push(relocationPreferenceFilter);
  }
  if (data.stageDateFromDate || data.stageDateToDate) {
    const relocationPreferenceFilter = {
      filterName: 'STAGE_STATUS_BETWEEN',
      values: [data.stageDateFromDate, data.stageDateToDate],
      fields: {},
    };
    filters.push(relocationPreferenceFilter);
  }
  if (!isEmpty(data.STAGE_STATUS)) {
    const stateStatusFilter = {
      filterName: 'STAGE_STATUS',
      values: [],
      fields: data.STAGE_STATUS,
    };
    filters.push(stateStatusFilter);
  }

  return filters;
};

export interface BusinessDefCount {
  id: string;
  businessDefinitionName: string;
  count: number;
}
export const jobStatusOptions = [
  {
    value: 'Opened',
    label: 'Open',
  },
  {
    value: 'Closed',
    label: 'Closed',
  },
  {
    value: 'All',
    label: 'All',
  },
];

export interface ITokens {
  token: string;
  auth: string;
}

export const getUpdatedquery = (searchQuery) => {
  const modifiedSearchQuery = {
    ...searchQuery,
    filters: searchQuery.filters.map((filter) => {
      if (
        filter.filterName === 'SUBMITTED_TEAM_ASSIGNED_JOB' ||
        filter.filterName === 'SUBMITTED_TO_JOB_POSTED_TEAM'
      ) {
        return {
          ...filter,
          fields: {},
        };
      } else if (filter.filterName === 'BUSINESS_UNIT') {
        return {
          ...filter,
          values: [extractBusinessUnitID(filter.values[0] as string, false)],
        };
      } else if (filter.filterName === 'STAGE_STATUS') {
        const { fields } = filter;
        if (Array.isArray(fields)) {
          const query = fields.reduce<Record<string, string[]>>(
            (result, { applicationStage, applicationState }) => {
              (result[applicationStage] = result[applicationStage] || []).push(applicationState);
              return result;
            },
            {}
          );

          return { ...filter, fields: query };
        }

        return filter;
      }
      return filter;
    }),
  };
  return modifiedSearchQuery;
};

export const getFilterArray = (searchQuery) => {
  const filterNameArray = searchQuery.filters.map((filter) => {
    if (
      filter.filterName === 'SUBMITTED_TEAM_ASSIGNED_JOB' ||
      filter.filterName === 'SUBMITTED_TO_JOB_POSTED_TEAM'
    ) {
      return {
        filterName: filter.filterName,
        values: filter.fields,
      };
    } else if (filter.filterName === 'BUSINESS_UNIT') {
      return {
        filterName: filter.filterName,
        values: [extractBusinessUnitID(filter.values[0] as string, true)],
      };
    }
    if (filter.filterName === 'JOB_LOCATIONS' || filter.filterName === 'CANDIDATE_LOCATIONS') {
      return {
        filterName: filter.filterName,
        values: [
          filter?.locations
            ?.map((field) => field?.city ?? field?.state ?? field?.country)
            ?.filter(Boolean)
            ?.join(', '),
        ],
      };
    }
    if (filter.filterName === 'SALARY') {
      return {
        filterName: filter.filterName,
        values: [
          `${filter?.fields?.minSalary}-${filter?.fields?.maxSalary} ${filter?.fields?.salaryCurrency} ${filter?.fields?.salaryTimeSpan}`,
        ],
      };
    }
    if (filter.filterName === 'JOB_TYPE') {
      return {
        filterName: filter.filterName,
        values: [filter.values],
      };
    }
    if (filter.filterName === 'SUBMISSION_X_HOURS_JOB_POSTING') {
      return {
        filterName: filter.filterName,
        values: [`${filter?.fields?.from}-${filter?.fields?.to}`],
      };
    }
    if (filter.filterName === 'RELOCATION_PREFERENCE') {
      return {
        filterName: filter.filterName,
        values: [filter.values],
      };
    }
    if (filter.filterName === 'SUBMITTED_OR_UPDATED_BETWEEN') {
      return {
        filterName: 'SubmittedOrUpdatedbetween',
        values: [
          `${filter?.values[0]},${formateDateInDateMonthYear(
            filter?.values[1]
          )}-${formateDateInDateMonthYear(filter?.values[2])}`,
        ],
      };
    } else {
      return {
        filterName: filter.filterName,
        values: filter.values ?? (filter?.fields && Object.values(filter?.fields).join('-')),
      };
    }
  });
  return filterNameArray;
};

export const decodeFromBase64 = (encodedString) => JSON.parse(atob(encodedString));

export const encodeToBase64 = (data) => btoa(JSON.stringify(data));

export const generateSubmissionBackwardUrl = (): IBreadCrumbsList[] => [
  { label: 'Submissions', url: `${window.location.pathname}${window.location.search}` },
];

export const getBusinessDefination = (id: string, businessDefinationOptions: WsSelectObj[]) =>
  businessDefinationOptions.find((bd) => bd.value === id);

type SortDirection = 'asc' | 'desc';

export const getSortModel = (sortString: string) => {
  const [field, sort] = sortString.split(',');
  return {
    field,
    sort: (sort as SortDirection) ?? 'asc',
  };
};

export const getDefaultSelectedSubmissionType = (
  searchQuery: ICandidateSubmissionSearchFilterVM,
  curRecruiterEmail: string
): string => {
  const hasMyQueue = searchQuery.filters.some((filter) => filter.filterName === 'MY_QUEUE');

  if (hasMyQueue) return 'my_queue';

  const submittedFilter = searchQuery.filters.find(
    (filter) => filter.filterName === 'SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB'
  );

  const isMySubmission =
    submittedFilter?.values.length === 1 && submittedFilter.values[0] === curRecruiterEmail;

  if (isMySubmission) return 'my_submissions';

  return 'all_submissions';
};

export const getDefaultQueueTab = (searchQuery: ICandidateSubmissionSearchFilterVM): number => {
  const myQueueFilter = searchQuery.filters.find((filter) => filter.filterName === 'MY_QUEUE');
  return myQueueFilter?.values?.[0] === 'ASSIGNED' ? 1 : 0;
};
